import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Download, Save, CheckCircle2, Plus, Trash2, Shield, Copy } from 'lucide-react';
import RouterOSVersionSelector from '../components/RouterOSVersionSelector';
import { generateFailoverScript } from '../lib/generators/failover';
import { saveToHistory } from '../lib/storage/database';

const FailoverWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [generatedScript, setGeneratedScript] = useState('');
  
  const [formData, setFormData] = useState({
    wans: [
      {
        puerto: 'ether1',
        tipo: 'dhcp',
        ip: '',
        gateway: '',
        netmask: '255.255.255.0',
        prioridad: 1,
        check_gateway: '8.8.8.8'
      }
    ],
    puertos_lan: 4,
    router: {
      nombre: '',
      zona_horaria: 'America/Mexico_City',
      subnet_lan: '192.168.1.0/24',
      dns_servers: ['8.8.8.8', '8.8.4.4']
    },
    routeros_version: 'v6'
  });

  const totalSteps = 6;

  const addWAN = () => {
    const nextPort = `ether${formData.wans.length + 1}`;
    const nextPriority = Math.max(...formData.wans.map(w => w.prioridad)) + 1;
    
    setFormData({
      ...formData,
      wans: [...formData.wans, {
        puerto: nextPort,
        tipo: 'dhcp',
        ip: '',
        gateway: '',
        netmask: '255.255.255.0',
        prioridad: nextPriority,
        check_gateway: '8.8.8.8'
      }]
    });
  };

  const removeWAN = (index) => {
    if (formData.wans.length > 1) {
      const newWans = formData.wans.filter((_, i) => i !== index);
      // Reorder priorities
      newWans.forEach((wan, i) => {
        wan.prioridad = i + 1;
      });
      setFormData({
        ...formData,
        wans: newWans
      });
    }
  };

  const updateWAN = (index, field, value) => {
    const newWans = [...formData.wans];
    newWans[index][field] = value;
    setFormData({
      ...formData,
      wans: newWans
    });
  };

  const generateScript = async () => {
    setLoading(true);
    try {
      const config = {
        wans: formData.wans.map(wan => ({
          puerto: wan.puerto,
          tipo: wan.tipo,
          ip: wan.ip || '',
          gateway: wan.gateway || '',
          prioridad: wan.prioridad,
          checkGateway: wan.check_gateway
        })),
        router: {
          nombre: formData.router.nombre,
          zonaHoraria: formData.router.zona_horaria,
          subnetLan: formData.router.subnet_lan,
          dnsServers: formData.router.dns_servers
        },
        puertosLan: formData.puertos_lan,
        routerosVersion: formData.routeros_version
      };
      
      const script = generateFailoverScript(config);
      setGeneratedScript(script);
      
      saveToHistory({
        script,
        type: 'failover',
        config,
        nombre: `Failover ${formData.wans.length} WANs - ${formData.router.nombre}`
      });
      
      setCurrentStep(6);
      copyToClipboard(script);
      alert('✅ Script generado y copiado al portapapeles');
    } catch (error) {
      console.error('Error:', error);
      alert('❌ Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('✅ Script copiado al portapapeles');
    }).catch(err => {
      console.error('Error al copiar:', err);
      alert('❌ Error al copiar el script');
    });
  };

  const downloadScript = () => {
    const blob = new Blob([generatedScript], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `failover_${formData.wans.length}wans_${Date.now()}.rsc`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const nextStep = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    } else if (currentStep === totalSteps - 1) {
      generateScript(false);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.router.nombre.trim() !== '';
      case 2:
        // Validar que haya al menos 2 WANs (1 principal + 1 respaldo)
        if (formData.wans.length < 2) {
          return false;
        }
        return formData.wans.every(wan => {
          if (wan.tipo === 'fijo') {
            return wan.puerto && wan.ip && wan.gateway;
          }
          return wan.puerto;
        });
      case 3:
        return formData.wans.every(wan => wan.check_gateway);
      case 4:
        return formData.router.subnet_lan;
      case 5:
        return true;
      default:
        return true;
    }
  };

  const getPriorityLabel = (priority) => {
    if (priority === 1) return 'Principal';
    return `Respaldo ${priority - 1}`;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <Shield className="w-16 h-16 mx-auto mb-4 text-blue-500" />
              <h2 className="text-3xl font-bold text-white mb-2">Configuración Failover</h2>
              <p className="text-gray-400">Respaldo automático de conexiones WAN</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nombre del Router *
              </label>
              <input
                type="text"
                value={formData.router.nombre}
                onChange={(e) => setFormData({
                  ...formData,
                  router: { ...formData.router, nombre: e.target.value }
                })}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Ej: Router_Failover_Principal"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Zona Horaria
              </label>
              <select
                value={formData.router.zona_horaria}
                onChange={(e) => setFormData({
                  ...formData,
                  router: { ...formData.router, zona_horaria: e.target.value }
                })}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="America/Mexico_City">America/Mexico_City (GMT-6)</option>
                <option value="America/Cancun">America/Cancun (GMT-5)</option>
                <option value="America/Tijuana">America/Tijuana (GMT-8)</option>
                <option value="America/Chihuahua">America/Chihuahua (GMT-7)</option>
              </select>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">Configuración de WANs</h2>
              <p className="text-gray-400">
                Configura tus conexiones WAN. La primera será la principal y las demás serán respaldo.
              </p>
              
              {formData.wans.length < 2 && (
                <div className="mt-4 p-4 bg-yellow-900/30 border border-yellow-600/50 rounded-lg">
                  <p className="text-yellow-400 text-sm">
                    ⚠️ Necesitas agregar al menos <strong>1 WAN de respaldo</strong> para continuar.
                    El failover requiere mínimo 2 conexiones (1 principal + 1 respaldo).
                  </p>
                </div>
              )}
            </div>

            {formData.wans.map((wan, index) => (
              <div key={index} className="bg-gray-700 p-6 rounded-lg space-y-4">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-white">
                      WAN {index + 1} - {getPriorityLabel(wan.prioridad)}
                    </h3>
                    <p className="text-sm text-gray-400">Prioridad: {wan.prioridad}</p>
                  </div>
                  {formData.wans.length > 1 && (
                    <button
                      onClick={() => removeWAN(index)}
                      className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Puerto</label>
                    <select
                      value={wan.puerto}
                      onChange={(e) => updateWAN(index, 'puerto', e.target.value)}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                    >
                      {[1, 2, 3, 4, 5].map(n => (
                        <option key={n} value={`ether${n}`}>ether{n}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-gray-300 mb-2">Tipo de Conexión</label>
                    <select
                      value={wan.tipo}
                      onChange={(e) => updateWAN(index, 'tipo', e.target.value)}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                    >
                      <option value="dhcp">DHCP (Automática)</option>
                      <option value="fijo">IP Fija</option>
                    </select>
                  </div>
                </div>

                {wan.tipo === 'fijo' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-300 mb-2">IP *</label>
                      <input
                        type="text"
                        value={wan.ip}
                        onChange={(e) => updateWAN(index, 'ip', e.target.value)}
                        className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                        placeholder="192.168.100.2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-300 mb-2">Gateway *</label>
                      <input
                        type="text"
                        value={wan.gateway}
                        onChange={(e) => updateWAN(index, 'gateway', e.target.value)}
                        className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                        placeholder="192.168.100.1"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}

            <button
              onClick={addWAN}
              className="w-full py-3 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:border-blue-500 hover:text-blue-500 transition-colors flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Agregar WAN de Respaldo
            </button>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">Monitoreo de Conectividad</h2>
              <p className="text-gray-400">
                Configura las IPs que se usarán para verificar la conectividad de cada WAN
              </p>
            </div>

            {formData.wans.map((wan, index) => (
              <div key={index} className="bg-gray-700 p-6 rounded-lg space-y-4">
                <h3 className="text-lg font-semibold text-white">
                  WAN {index + 1} - {getPriorityLabel(wan.prioridad)}
                </h3>
                
                <div>
                  <label className="block text-sm text-gray-300 mb-2">
                    IP para Check Gateway (Ping)
                  </label>
                  <input
                    type="text"
                    value={wan.check_gateway}
                    onChange={(e) => updateWAN(index, 'check_gateway', e.target.value)}
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white"
                    placeholder="8.8.8.8"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    El router hará ping a esta IP para verificar si la WAN está activa
                  </p>
                </div>

                <div className="bg-gray-800 p-3 rounded-lg">
                  <p className="text-xs text-gray-400">
                    Sugerencias: 8.8.8.8 (Google DNS), 1.1.1.1 (Cloudflare), 208.67.222.222 (OpenDNS)
                  </p>
                </div>
              </div>
            ))}
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">Configuración LAN</h2>
              <p className="text-gray-400">Red local y puertos LAN</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Subred LAN
              </label>
              <input
                type="text"
                value={formData.router.subnet_lan}
                onChange={(e) => setFormData({
                  ...formData,
                  router: { ...formData.router, subnet_lan: e.target.value }
                })}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="192.168.1.0/24"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Cantidad de Puertos LAN
              </label>
              <select
                value={formData.puertos_lan}
                onChange={(e) => setFormData({
                  ...formData,
                  puertos_lan: parseInt(e.target.value)
                })}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                  <option key={n} value={n}>{n} puerto{n > 1 ? 's' : ''}</option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">
                Los puertos LAN se agregarán al bridge automáticamente
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Servidores DNS
              </label>
              <div className="space-y-2">
                <input
                  type="text"
                  value={formData.router.dns_servers[0]}
                  onChange={(e) => {
                    const newDNS = [...formData.router.dns_servers];
                    newDNS[0] = e.target.value;
                    setFormData({
                      ...formData,
                      router: { ...formData.router, dns_servers: newDNS }
                    });
                  }}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  placeholder="DNS Primario"
                />
                <input
                  type="text"
                  value={formData.router.dns_servers[1]}
                  onChange={(e) => {
                    const newDNS = [...formData.router.dns_servers];
                    newDNS[1] = e.target.value;
                    setFormData({
                      ...formData,
                      router: { ...formData.router, dns_servers: newDNS }
                    });
                  }}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                  placeholder="DNS Secundario"
                />
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">Revisión de Configuración</h2>
              <p className="text-gray-400">Verifica los datos antes de generar el script</p>
            </div>

            <div className="bg-gray-700 p-6 rounded-lg space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Router</h3>
                <p className="text-gray-300">Nombre: {formData.router.nombre}</p>
                <p className="text-gray-300">Zona Horaria: {formData.router.zona_horaria}</p>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-2">WANs Configuradas</h3>
                {formData.wans.map((wan, index) => (
                  <div key={index} className="bg-gray-800 p-4 rounded-lg mb-2">
                    <p className="text-white font-medium">
                      WAN {index + 1} - {getPriorityLabel(wan.prioridad)}
                    </p>
                    <p className="text-gray-400 text-sm">Puerto: {wan.puerto}</p>
                    <p className="text-gray-400 text-sm">Tipo: {wan.tipo === 'dhcp' ? 'DHCP' : 'IP Fija'}</p>
                    {wan.tipo === 'fijo' && (
                      <>
                        <p className="text-gray-400 text-sm">IP: {wan.ip}</p>
                        <p className="text-gray-400 text-sm">Gateway: {wan.gateway}</p>
                      </>
                    )}
                    <p className="text-gray-400 text-sm">Check Gateway: {wan.check_gateway}</p>
                  </div>
                ))}
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Red LAN</h3>
                <p className="text-gray-300">Subred: {formData.router.subnet_lan}</p>
                <p className="text-gray-300">Puertos LAN: {formData.puertos_lan}</p>
                <p className="text-gray-300">DNS: {formData.router.dns_servers.join(', ')}</p>
              </div>
            </div>

            <div className="bg-blue-900/20 border border-blue-500 p-4 rounded-lg">
              <h4 className="text-blue-400 font-semibold mb-2">¿Cómo funciona el Failover?</h4>
              <ul className="text-sm text-gray-300 space-y-1">
                <li>• La WAN principal (prioridad 1) se usa por defecto</li>
                <li>• El router hace ping constante al check gateway de cada WAN</li>
                <li>• Si la principal falla, cambia automáticamente al respaldo</li>
                <li>• Cuando la principal se recupera, el tráfico vuelve a ella</li>
              </ul>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <h2 className="text-3xl font-bold text-white mb-2">¡Script Generado!</h2>
              <p className="text-gray-400">Tu configuración de failover está lista</p>
            </div>

            <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden mb-6">
              <div className="bg-gray-900 px-4 py-3 flex justify-between items-center">
                <span className="text-sm text-gray-400">Script Generado y Guardado</span>
                <button
                  onClick={() => copyToClipboard(generatedScript)}
                  className="flex items-center gap-2 px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm transition-colors"
                >
                  <Copy className="w-4 h-4" />
                  Copiar
                </button>
              </div>
              <pre className="p-4 text-sm text-gray-300 overflow-x-auto max-h-96 overflow-y-auto">
                {generatedScript}
              </pre>
            </div>

            <button
              onClick={() => navigate('/scripts')}
              className="w-full px-6 py-3 bg-gray-600 hover:bg-gray-700 rounded-lg font-semibold transition-colors"
            >
              Nueva Configuración
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate('/scripts')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver
          </button>
          <div className="text-gray-400 text-sm">
            Paso {currentStep} de {totalSteps}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="bg-gray-800 rounded-xl shadow-2xl p-8 mb-8">
          {renderStepContent()}
        </div>

        {/* Navigation */}
        {currentStep < totalSteps && (
          <div className="flex justify-between gap-4">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className="flex items-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="w-5 h-5" />
              Anterior
            </button>
            <button
              onClick={nextStep}
              disabled={!canProceed() || loading}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {currentStep === totalSteps - 1 ? (
                <>
                  {loading ? 'Generando...' : 'Generar Script'}
                  <CheckCircle2 className="w-5 h-5" />
                </>
              ) : (
                <>
                  Siguiente
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FailoverWizard;
